
import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../firebaseConfig"; // Make sure you have this file with firebase config
import ErrorMessage from "../components/common/ErrorMessage";
import { CONSTANTS } from "../constants/Constants";

const HomePage = () => {
  // State for storing dynamic content
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentAnnouncementIndex, setCurrentAnnouncementIndex] = useState(0);
  
  // State for consultation form
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    query: ""
  });
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [formSuccess, setFormSuccess] = useState(false);
  const [formError, setFormError] = useState(null);

  // Fetch homepage content from API
  useEffect(() => {
    const fetchHomepageContent = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${CONSTANTS.APIURL}/api/homepage`);
        console.log("Fetched homepage content:", response.data);
        setContent(response.data);
        setError(null);
      } catch (err) {
        console.error("Error fetching homepage content:", err);
        setError("Failed to load content. Please refresh the page.");
      } finally {
        setLoading(false);
      }
    };

    fetchHomepageContent();
  }, []);

  // Auto-rotate announcement images
  useEffect(() => {
    if (!content || !content.announcements || content.announcements.length === 0)
      return;

    const interval = setInterval(() => {
      setCurrentAnnouncementIndex((prevIndex) =>
        prevIndex === content.announcements.length - 1 ? 0 : prevIndex + 1
      );
    }, 5000);

    return () => clearInterval(interval);
  }, [content]);

  // Handle form input changes
  const handleInputChange = (e) => {
    const { id, value } = e.target;
    setFormData({ ...formData, [id]: value });
  };

  // Handle form submission
  const handleFormSubmit = async (e) => {
    e.preventDefault();
    
    // Simple validation
    if (!formData.name || !formData.email || !formData.query) {
      setFormError("Please fill all required fields");
      return;
    }
    
    setFormSubmitting(true);
    setFormError(null);
    
    try {
      // Add document to Firestore
      await addDoc(collection(db, "consultationRequests"), {
        ...formData,
        timestamp: serverTimestamp(),
        status: "new"
      });
      
      // Reset form and show success
      setFormData({ name: "", phone: "", email: "", query: "" });
      setFormSuccess(true);
      
      // Reset success message after 5 seconds
      setTimeout(() => {
        setFormSuccess(false);
      }, 5000);
      
    } catch (err) {
      console.error("Error saving form data:", err);
      setFormError("Failed to submit your request. Please try again.");
    } finally {
      setFormSubmitting(false);
    }
  };

  // Show loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-6 flex justify-center items-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#2C5282]"></div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return <ErrorMessage message={error} />;
  }

  // Show empty state if no content (fallback)
  if (!content) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-[#2C5282]">
            Welcome to SKYSEP
          </h2>
          <p className="mt-2 text-gray-600">
            Content is being updated. Please check back soon.
          </p>
        </div>
      </div>
    );
  }

  // Destructure the content for easier access
  const {
    hero,
    stats,
    features,
    testimonials,
    upcomingBatches,
    focusAreas,
    sectionTitles,
    cta,
    announcements = [] // Default to empty array if not provided
  } = content;

  // Sample announcement data if not provided by API
  const defaultAnnouncements = [
    {
      id: "ann1",
      title: "New Smart Learning Program",
      description: "Experience our cutting-edge approach to education with personalized learning paths",
      image: "https://images.unsplash.com/photo-1509062522246-3755977927d7?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&h=750&q=80",
      link: "/programs/smart-learning"
    },
    {
      id: "ann2",
      title: "Summer Bootcamp Registration Open",
      description: "Accelerate your learning with our intensive summer programs",
      image: "https://images.unsplash.com/photo-1523240795612-9a054b0db644?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&h=750&q=80",
      link: "/bootcamps"
    },
    {
      id: "ann3",
      title: "Scholarship Opportunities Available",
      description: "Apply now for merit-based scholarships for all our programs",
      image: "https://images.unsplash.com/photo-1427504494785-3a9ca7044f45?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&h=750&q=80",
      link: "/scholarships"
    }
  ];

  // Use announcements from API or default if not available
  const displayAnnouncements = announcements.length > 0 ? announcements : defaultAnnouncements;

  return (
    <div className="pt-16">
      {/* Hero Section - Deep blue with gradient */}
      <section className="relative overflow-hidden bg-gradient-to-br from-[#2C5282] to-[#1A365D] text-white">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-[#2C5282] opacity-90"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tight mb-6">
                {hero?.title ? (
                  hero.title.includes("Success") ? (
                    <>
                      {hero.title.split("Success")[0]}
                      <span className="text-white underline decoration-yellow-400 decoration-4">Success</span>
                      {hero.title.split("Success")[1]}
                    </>
                  ) : (
                    hero.title
                  )
                ) : (
                  "Transform Your Future with SKYSEP"
                )}
              </h1>
              <p className="text-lg md:text-xl mb-8 text-blue-100">
                {hero?.subtitle || "Personalized education programs designed to unlock your full potential and accelerate your career growth."}
              </p>
              <div style={{height:20}}/>

              <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4">
                <Link
                  to="/classroom"
                  className="inline-flex items-center justify-center px-8 py-3 border border-transparent text-base font-medium rounded-md text-[#2C5282] bg-white hover:bg-gray-100 transition-colors duration-300 shadow-lg hover:shadow-xl"
                >
                  Enroll Now
                </Link>
                <Link
                  to="/watch-session/683ef9a271bed98aea1797e6"
                  className="inline-flex items-center justify-center px-8 py-3 border-2 border-white text-base font-medium rounded-md text-white hover:bg-white/10 transition-colors duration-300"
                >
                  Free Demo Class
                </Link>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="hidden md:block relative"
            >
              {/* <img
                src={hero?.heroImage}
                alt="SKYSEP Students"
                className="rounded-lg shadow-2xl w-full"
              /> */}
              {hero?.resultsBox && (
                <div className="absolute -bottom-6 -right-6 bg-white text-[#2C5282] rounded-lg p-6 shadow-xl">
                  <p className="text-sm font-bold">{hero?.resultsBox.title}</p>
                  <p className="text-3xl font-extrabold">
                    {hero?.resultsBox.value}
                  </p>
                </div>
              )}
            </motion.div>
          </div>
        </div>

        {/* Animated wave divider */}
        {/* <div className="absolute bottom-0 left-0 right-0">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 200" className="w-full h-auto">
            <path fill="#ffffff" fillOpacity="1" d="M0,64L48,80C96,96,192,128,288,138.7C384,149,480,139,576,122.7C672,107,768,85,864,96C960,107,1056,149,1152,154.7C1248,160,1344,128,1392,112L1440,96L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"></path>
          </svg>
        </div> */}
      </section>

      {/* Announcement Section - New Addition */}
      <section className="py-8 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <motion.h2
              className="text-3xl font-extrabold text-[#2C5282] sm:text-4xl"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              Latest Announcements
            </motion.h2>
            <motion.p
              className="mt-4 max-w-2xl text-xl text-gray-600 mx-auto"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              Stay updated with our latest programs and opportunities
            </motion.p>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="relative overflow-hidden rounded-2xl shadow-xl"
          >
            <AnimatePresence mode="wait">
              <motion.div
                key={currentAnnouncementIndex}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.5 }}
                className="relative aspect-[21/9]"
              >
                <img
                  src={displayAnnouncements[currentAnnouncementIndex].image}
                  alt={displayAnnouncements[currentAnnouncementIndex].title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#2C5282]/90 via-[#2C5282]/40 to-transparent flex items-end">
                  <div className="p-8 md:p-12 text-white max-w-3xl">
                    <div className="mb-3 inline-block px-3 py-1 bg-yellow-400 text-[#2C5282] text-sm font-bold rounded-full">
                      New Announcement
                    </div>
                    <h3 className="text-2xl md:text-4xl font-bold mb-3">
                      {displayAnnouncements[currentAnnouncementIndex].title}
                    </h3>
                    <p className="text-lg md:text-xl text-gray-100 mb-6">
                      {displayAnnouncements[currentAnnouncementIndex].description}
                    </p>
                    <Link
                      to={displayAnnouncements[currentAnnouncementIndex].link}
                      className="inline-flex items-center justify-center px-6 py-3 bg-white text-[#2C5282] text-base font-medium rounded-md hover:bg-gray-100 transition-colors duration-300 shadow-lg"
                    >
                      Learn More
                      <svg className="w-5 h-5 ml-2" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                      </svg>
                    </Link>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>

            {/* Navigation dots */}
            <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-3">
              {displayAnnouncements.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentAnnouncementIndex(index)}
                  className={`w-3 h-3 rounded-full transition-all duration-300 ${
                    index === currentAnnouncementIndex ? "bg-white w-6" : "bg-white/50"
                  }`}
                  aria-label={`View announcement ${index + 1}`}
                />
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Stats Section - Clean white with blue accents - Enhanced version */}
      {stats && stats.length > 0 && (
        <section className="bg-gray-50 py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <motion.h2
                className="text-3xl font-extrabold text-[#2C5282] sm:text-4xl"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                Our Impact in Numbers
              </motion.h2>
              <motion.p
                className="mt-4 max-w-2xl text-xl text-gray-600 mx-auto"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
              >
                We take pride in our achievements and the success of our students
              </motion.p>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {stats.map((item, index) => (
                <motion.div
                  key={item.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="bg-white rounded-lg shadow-sm hover:shadow-md transition-all duration-300 p-6 text-center border-t-4 border-[#2C5282]"
                >
                  <p className="text-4xl md:text-5xl font-extrabold text-[#2C5282] mb-3">
                    {item.stat}
                  </p>
                  <p className="mt-2 text-lg font-medium text-gray-600">
                    {item.title}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Features Section - Light gray background with card design */}
      {features && features.length > 0 && (
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <motion.h2
                className="text-3xl font-extrabold text-[#2C5282] sm:text-4xl"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                {sectionTitles?.features?.title ||
                  "Holistic Solutions for Personal Growth"}
              </motion.h2>
              <motion.p
                className="mt-4 max-w-2xl text-xl text-gray-600 mx-auto"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
              >
                {sectionTitles?.features?.subtitle ||
                  "Everything you need for your personal and professional development"}
              </motion.p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {features.map((feature, index) => (
                <motion.div
                  key={feature.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="relative bg-white p-8 rounded-xl shadow-sm hover:shadow-lg transition-shadow duration-300 border border-gray-100"
                >
                  <div className="absolute -top-4 left-8 w-12 h-12 rounded-lg bg-gradient-to-br from-[#2C5282] to-[#1A365D] flex items-center justify-center text-white shadow-lg">
                    <div
                      dangerouslySetInnerHTML={{ __html: feature.iconSvg }}
                      className="w-6 h-6"
                    />
                  </div>
                  <div className="mt-8">
                    <h3 className="text-xl font-bold text-[#2C5282] mb-3 flex items-center">
                      {feature.title}
                      {feature.isNew && (
                        <span className="ml-2 px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-[#2C5282]">
                          NEW
                        </span>
                      )}
                    </h3>
                    <p className="text-gray-600 mb-4">{feature.description}</p>
                    <Link
                      to={feature.link}
                      className="text-[#2C5282] hover:text-[#1A365D] font-medium inline-flex items-center transition-colors duration-300"
                    >
                      Learn more
                      <svg
                        className="w-4 h-4 ml-1"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </Link>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Success Stories Section - Blue gradient */}
      {testimonials && testimonials.length > 0 && (
        <section className="py-20 bg-gradient-to-br from-[#2C5282] to-[#1A365D] text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <motion.h2
                className="text-3xl font-extrabold sm:text-4xl"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                {sectionTitles?.testimonials?.title || "Our Success Stories"}
              </motion.h2>
              <motion.p
                className="mt-4 max-w-2xl text-xl text-blue-100 mx-auto"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
              >
                {sectionTitles?.testimonials?.subtitle ||
                  "Hear from our clients who have achieved their personal and professional goals"}
              </motion.p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {testimonials.map((testimonial, index) => (
                <motion.div
                  key={testimonial.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="bg-white/10 backdrop-blur-sm p-8 rounded-xl border border-white/20 hover:border-white/40 transition-colors duration-300"
                >
                  <div className="flex items-center mb-6">
                    <div className="mr-4 h-16 w-16 rounded-full overflow-hidden border-2 border-white/50">
                      <img
                        src={testimonial.image}
                        alt={testimonial.author}
                        className="h-full w-full object-cover"
                      />
                    </div>
                    <div>
                      <h3 className="font-bold text-lg">
                        {testimonial.author}
                      </h3>
                      <p className="text-blue-200 font-medium">
                        {testimonial.rank}
                      </p>
                    </div>
                  </div>
                  <p className="italic text-blue-100">
                    "{testimonial.content}"
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Upcoming Programs Section - Clean design */}
      {upcomingBatches && upcomingBatches.length > 0 && (
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <motion.h2
                className="text-3xl font-extrabold text-[#2C5282] sm:text-4xl"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                {sectionTitles?.upcomingBatches?.title || "Upcoming Programs"}
              </motion.h2>
              <motion.p
                className="mt-4 max-w-2xl text-xl text-gray-600 mx-auto"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
              >
                {sectionTitles?.upcomingBatches?.subtitle ||
                  "Secure your spot in our upcoming programs and start your journey to personal growth"}
              </motion.p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {upcomingBatches.map((batch, index) => (
                <motion.div
                  key={batch.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="bg-white p-8 rounded-xl border border-gray-200 hover:shadow-lg transition-all duration-300"
                >
                  <div className="mb-4">
                    <span
                      className="inline-block px-3 py-1 bg-blue-100 text-[#2C5282] text-sm font-medium rounded-full"
                    >
                      {batch.tag}
                    </span>
                  </div>
                  <h3 className="text-xl font-bold text-[#2C5282] mb-3">
                    {batch.title}
                  </h3>
                  <p className="text-gray-600 mb-6">{batch.description}</p>
                  <div className="space-y-3 mb-8">
                    <div className="flex items-center text-gray-700">
                      <svg
                        className="w-5 h-5 text-[#2C5282] mr-3"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z"
                          clipRule="evenodd"
                        />
                      </svg>
                      <span>Duration: {batch.duration}</span>
                    </div>
                    <div className="flex items-center text-gray-700">
                      <svg
                        className="w-5 h-5 text-[#2C5282] mr-3"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z"
                          clipRule="evenodd"
                        />
                      </svg>
                      <span>Starts: {batch.starts}</span>
                    </div>
                    <div className="flex items-center text-gray-700">
                      <svg
                        className="w-5 h-5 text-[#2C5282] mr-3"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path d="M8.433 7.418c.155-.103.346-.196.567-.267v1.698a2.305 2.305 0 01-.567-.267C8.07 8.34 8 8.114 8 8c0-.114.07-.34.433-.582zM11 12.849v-1.698c.22.071.412.164.567.267.364.243.433.468.433.582 0 .114-.07.34-.433.582a2.305 2.305 0 01-.567.267z"/>
                        <path
                          fillRule="evenodd" 
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-13a1 1 0 10-2 0v.092a4.535 4.535 0 00-1.676.662C6.602 6.234 6 7.009 6 8c0 .99.602 1.765 1.324 2.246.48.32 1.054.545 1.676.662v1.941c-.391-.127-.68-.317-.843-.504a1 1 0 10-1.51 1.31c.562.649 1.413 1.076 2.353 1.253V15a1 1 0 102 0v-.092a4.535 4.535 0 001.676-.662C13.398 13.766 14 12.991 14 12c0-.99-.602-1.765-1.324-2.246A4.535 4.535 0 0011 9.092V7.151c.391.127.68.317.843.504a1 1 0 101.511-1.31c-.563-.649-1.413-1.076-2.354-1.253V5z"
                          clipRule="evenodd"
                        />
                      </svg>
                      <span>Fee: {batch.fee}</span>
                    </div>
                  </div>
                  <Link
                    to="/classroom"
                    className="block w-full bg-[#2C5282] text-white text-center py-3 rounded-md font-medium hover:bg-[#1A365D] transition-colors duration-300"
                  >
                    Enroll Now
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Focus Areas Section - Clean cards with images */}
      {focusAreas && focusAreas.length > 0 && (
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <motion.h2
                className="text-3xl font-extrabold text-[#2C5282] sm:text-4xl"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                {sectionTitles?.focusAreas?.title || "Our Specialized Services"}
              </motion.h2>
              <motion.p
                className="mt-4 max-w-2xl text-xl text-gray-600 mx-auto"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
              >
                {sectionTitles?.focusAreas?.subtitle ||
                  "Our specialized programs to help you achieve holistic growth"}
              </motion.p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {focusAreas.map((area, index) => (
                <motion.div
                  key={area.id}
                  initial={{ opacity: 0, x: index % 2 === 0 ? -20 : 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-lg transition-all duration-300 border border-gray-100"
                >
                  <div className="flex flex-col md:flex-row">
                    <div className="md:w-2/5">
                      <img
                        src={area.image}
                        alt={area.title}
                        className="h-full w-full object-cover"
                      />
                    </div>
                    <div className="md:w-3/5 p-8">
                      <h3 className="text-xl font-bold text-[#2C5282] mb-3">
                        {area.title}
                      </h3>
                      <p className="text-gray-600 mb-6">{area.description}</p>
                      <Link
                        to={area.link}
                        className="text-[#2C5282] hover:text-[#1A365D] font-medium inline-flex items-center transition-colors duration-300"
                      >
                        View Program
                        <svg
                          className="w-4 h-4 ml-1"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path
                            fillRule="evenodd"
                            d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </Link>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Call to Action Section - Blue gradient with Firestore integration */}
      <section className="py-20 bg-gradient-to-br from-[#2C5282] to-[#1A365D] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <h2 className="text-3xl md:text-4xl font-extrabold mb-6">
                {cta?.title || "Begin Your Journey to Personal Growth Today"}
              </h2>
              <p className="text-lg text-blue-100 mb-8">
                {cta?.description ||
                  "Join our community of individuals who have achieved their personal and professional goals with our holistic approach."}
              </p>
              <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4">
                <Link
                  to="/classroom"
                  className="inline-flex items-center justify-center px-8 py-3 border border-transparent text-base font-medium rounded-md text-[#2C5282] bg-white hover:bg-gray-100 transition-colors duration-300 shadow-lg hover:shadow-xl"
                >
                  Enroll Now
                </Link>
                {/* <Link
                  to="/contact"
                  className="inline-flex items-center justify-center px-8 py-3 border-2 border-white text-base font-medium rounded-md text-white hover:bg-white/10 transition-colors duration-300"
                >
                  Contact Us
                </Link> */}
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="rounded-2xl bg-white/10 backdrop-blur-sm p-8 border border-white/20"
            >
              <h3 className="text-2xl font-bold mb-6">
                Request Free Consultation
              </h3>
              {formSuccess ? (
                <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded relative mb-6" role="alert">
                  <strong className="font-bold">Success! </strong>
                  <span className="block sm:inline">Your consultation request has been submitted. We'll contact you shortly.</span>
                </div>
              ) : (
                <form className="space-y-6" onSubmit={handleFormSubmit}>
                  {formError && (
                    <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
                      <span className="block sm:inline">{formError}</span>
                    </div>
                  )}
                  <div>
                    <label
                      htmlFor="name"
                      className="block text-sm font-medium text-blue-100 mb-2"
                    >
                      Full Name*
                    </label>
                    <input
                      type="text"
                      id="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-900 bg-white"
                      placeholder="Enter your full name"
                      required
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="phone"
                      className="block text-sm font-medium text-blue-100 mb-2"
                    >
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-900 bg-white"
                      placeholder="Enter your phone number"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="email"
                      className="block text-sm font-medium text-blue-100 mb-2"
                    >
                      Email Address*
                    </label>
                    <input
                      type="email"
                      id="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-900 bg-white"
                      placeholder="Enter your email address"
                      required
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="query"
                      className="block text-sm font-medium text-blue-100 mb-2"
                    >
                      Your Query*
                    </label>
                    <textarea
                      id="query"
                      value={formData.query}
                      onChange={handleInputChange}
                      rows={4}
                      className="w-full px-4 py-3 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-900 bg-white"
                      placeholder="Tell us how we can help you"
                      required
                    ></textarea>
                  </div>
                  <button
                    type="submit"
                    disabled={formSubmitting}
                    className="w-full bg-white text-[#2C5282] py-3 rounded-md font-medium hover:bg-gray-100 transition-colors duration-300 shadow-md hover:shadow-lg flex justify-center items-center"
                  >
                    {formSubmitting ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-[#2C5282] mr-3"></div>
                        Submitting...
                      </>
                    ) : (
                      "Request Callback"
                    )}
                  </button>
                </form>
              )}
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;